import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import axiosPrivate from 'config/axiosPrivate';

import '../admin/textDisplay.css';

// Redux Store
import {
  adminClientRedactionDataSelector,
  setCurrentRedaction,
} from 'store/AdminClient/RedactionData';
import { UserSessionSelector } from 'store/UserSession';

// Components
import { Form } from '@unform/web';
import { CustomEditor } from 'components/shared/CustomEditor';
import { Loader } from 'components/shared/Loader';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';

// Hooks
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useWordCountTracker } from 'utils/hooks/features/textDisplay/useWordCountTracker';
import { useAdminClientTextSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextSave';
import { useAdminClientAutoSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientAutoSave';
import { useAutosave } from 'utils/hooks/useAutoSave';
import usePageTracking from 'utils/hooks/usePageTracking';
import { useAdminClientDeliverText } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientDeliverText';
import { useAdminClientPutToDraft } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientPutToDraft';

import { ROLES } from 'utils/Constants';
import { removeStrongTags } from 'utils/StringUtils';

function AssignedTraductionTextContent() {
  const [isFetchLoading, setIsFetchLoading] = useState(true);
  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  // Form data
  const [textTitle, setTextTitle] = useState('');
  const [keyword, setKeyword] = useState('');
  const [url, setUrl] = useState('');
  const [titleTag, setTitleTag] = useState('');
  const [metaDescriptionTag, setMetaDescriptionTag] = useState('');
  const [initialHtmlContent, setInitialHtmlContent] = useState('');
  const [initialTextContent, setInitialTextContent] = useState('');
  const [actualHtmlContent, setActualHtmlContent] = useState('');
  const [actualTextContent, setActualTextContent] = useState('');
  const [textId, setTextId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [isTextDelivered, setIsTextDelivered] = useState(false);
  const [isTextSaved, setIsTextSaved] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const { currentRedaction } = useSelector(adminClientRedactionDataSelector);

  const formRef = useRef(null);

  // Editor management - use stepOneEditorRef for source, editorRef for translated
  const {
    editorRef: translatedEditorRef,
    stepOneEditorRef: sourceEditorRef,
    editorInitialized: translatedEditorInitialized,
    stepOneEditorInitialized: sourceEditorInitialized,
    handleEditorInit: handleTranslatedEditorInit,
    handleStepOneEditorInit: handleSourceEditorInit,
  } = useEditorManagement();

  // Word count tracking
  const {
    wordCount: sourceWordCount,
    debouncedSetWordCount: debouncedSetSourceWordCount,
  } = useWordCountTracker(sourceEditorRef, sourceEditorInitialized);

  const {
    wordCount: translatedWordCount,
    debouncedSetWordCount: debouncedSetTranslatedWordCount,
  } = useWordCountTracker(translatedEditorRef, translatedEditorInitialized);

  // Get textId from URL or localStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const textIdFromUrl = params.get('textId');
    const textIdFromStorage = localStorage.getItem('AdminClient_TextId');
    const finalTextId = textIdFromUrl || textIdFromStorage || currentRedaction?.id;
    
    if (finalTextId) {
      setTextId(finalTextId);
      loadTextData(finalTextId);
    } else {
      setIsFetchLoading(false);
    }
  }, [location.search, currentRedaction?.id]);

  // Load text data
  const loadTextData = async (id) => {
    if (!id) return;

    try {
      setIsFetchLoading(true);
      const res = await axiosPrivate.get(`/texts/external/${id}/fullDetails`);
      const d = res?.data || {};

      setTextId(d.id || id);
      setTextTitle(d.textTitle || d.keyword || '');
      setKeyword(d.keyword || '');
      setUrl(d.url || '');
      setTitleTag(d.titleTag || '');
      setMetaDescriptionTag(d.metaDescriptionTag || '');
      setInitialHtmlContent(d.initialHtmlContent || '');
      setInitialTextContent(d.initialTextContent || '');
      setActualHtmlContent(d.actualHtmlContent || '');
      setActualTextContent(d.actualTextContent || '');
      setProjectId(d.projectId || d.orderId || null);
      setIsTextDelivered(!!d.delivered);
      setIsTextSaved(!!(d.actualHtmlContent || d.actualTextContent));

      // Update Redux store
      dispatch(
        setCurrentRedaction({
          id: d.id || id,
          title: d.textTitle || d.keyword || '',
          textTitle: d.textTitle || d.keyword || '',
          keyword: d.keyword || '',
          url: d.url || '',
          titleTag: d.titleTag || '',
          metaDescriptionTag: d.metaDescriptionTag || '',
          initialHtmlContent: d.initialHtmlContent || '',
          initialTextContent: d.initialTextContent || '',
          actualHtmlContent: d.actualHtmlContent || '',
          actualTextContent: d.actualTextContent || '',
          type: d.type || 'TRANSLATED',
          textType: d.type || 'TRANSLATED',
          projectId: d.projectId || d.orderId || null,
          websiteId: d.websiteId || 0,
          aiAgentId: d.aiAgentId || '',
          delivered: !!d.delivered,
        })
      );
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
      setErrorSuccessMsg(t('Erreur lors du chargement des données du texte'));
      setIsSuccess(false);
      setErrorSuccess(true);
    } finally {
      setIsFetchLoading(false);
    }
  };

  // Save functionality
  const { handleSave } = useAdminClientTextSave({
    redactionData: currentRedaction || { id: textId },
    initialHtmlContent,
    initialTextContent,
    actualTextContent,
    actualHtmlContent,
    actualTextWordCount: translatedWordCount,
    initialTextWordCount: sourceWordCount,
    combinedKeywords: '',
    keyword,
    metaDescriptionTag,
    textTitle,
    titleTag,
    url,
    textType: 'TRANSLATED',
    stuffedKeywords: [],
    websites: '',
    initialStuffedKeywords: [],
    analyseResult: null,
    redactionId: textId,
    setConfirmation: () => {},
    setActualTextContent,
    setActualHtmlContent,
    setErrorSuccessMsg,
    setErrorSuccessImg: () => {},
    setIsSuccess,
    setErrorSuccess,
    t,
    websiteId: currentRedaction?.websiteId || 0,
    aiAgentId: currentRedaction?.aiAgentId || '',
    aiAgentModel: currentRedaction?.aiAgentModel || '',
    projectId: projectId,
    currentContentScore: null,
    currentSemanticGap: [],
  });

  const { deliverText } = useAdminClientDeliverText({
    t,
    textType: 'TRANSLATION',
    combinedKeywords: '',
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
    titleTag,
    metaDescriptionTag,
    setIsFetchLoading,
    actualHtmlContent,
    actualTextContent,
    semanticScore: 0,
    initialHtmlContent,
    initialTextContent,
    keyword,
    textTitle,
    actualTextWordCount: translatedWordCount,
    initialTextWordCount: sourceWordCount,
    url,
    websites: '',
    setErrorSuccessImg: () => {},
    redactionId: textId,
    formData: currentRedaction || { id: textId },
    currentContentScore: null,
  });

  const { putToDraft } = useAdminClientPutToDraft({
    t,
    actualHtmlContent,
    actualTextContent,
    analyseResult: null,
    keyword,
    metaDescriptionTag,
    list: [],
    combinedKeywords: '',
    websites: '',
    stuffedKeywords: [],
    textTitle,
    titleTag,
    url,
    textId: textId,
    projectId: projectId,
    initialTextContent,
    initialHtmlContent,
    initialStuffedKeywords: [],
    possibleStuffedKeywords: [],
    actualTextWordCount: translatedWordCount,
    initialTextWordCount: sourceWordCount,
    type: 'TRANSLATED',
    activeInitialText: true,
    totalNumberOfTextAdded: 0,
    contentScore: null,
    writerProjectId: 0,
    websiteId: currentRedaction?.websiteId || 0,
    aiAgentId: currentRedaction?.aiAgentId || '',
    currentSemanticGap: [],
    setErrorSuccessMsg,
    setErrorSuccessImg: () => {},
    setIsSuccess,
    setErrorSuccess,
  });

  const { autoSave } = useAdminClientAutoSave({
    textId: textId,
  });

  const { scheduleSave, isSaving } = useAutosave(autoSave, 30000);

  // Handle source editor changes (read-only, but track word count)
  const handleSourceEditorChange = (value, editor) => {
    const textValue = editor.getContent({ format: 'text' });
    const newWordCount = editor.plugins.wordcount.getCount();
    debouncedSetSourceWordCount(newWordCount);
    setInitialHtmlContent(value);
    setInitialTextContent(textValue);
  };

  // Handle translated editor changes
  const handleTranslatedEditorChange = (value, editor) => {
    const textValue = editor.getContent({ format: 'text' });
    const newWordCount = editor.plugins.wordcount.getCount();
    debouncedSetTranslatedWordCount(newWordCount);

    // Auto-save
    scheduleSave(
      textValue,
      value,
      currentRedaction || { id: textId },
      initialHtmlContent,
      initialTextContent,
      'SMART'
    );

    setActualHtmlContent(value);
    setActualTextContent(textValue);
    setIsTextSaved(true);
  };

  // Manual save
  const handleManualSave = async () => {
    try {
      await handleSave('manualDraft');
      setErrorSuccessMsg(t('Texte sauvegardé avec succès'));
      setIsSuccess(true);
      setErrorSuccess(true);
    } catch (error) {
      setErrorSuccessMsg(t('Erreur lors de la sauvegarde'));
      setIsSuccess(false);
      setErrorSuccess(true);
    }
  };

  useEffect(() => {
    document.title = t('Typix - Traduction Smart');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem('AdminClient_TextId');
    };
  }, []);

  if (isFetchLoading) return <Loader />;

  if (!textId) {
    return (
      <div className="content-center">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>❌ Aucun texte trouvé</h3>
          <p>Impossible de charger les données du texte</p>
          <button
            onClick={() => history.push('/AdminClient/DetailsProject')}
            className="btn btn-primary"
          >
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-center">
      <Form
        ref={formRef}
        style={{
          transition: 'margin-right 0.3s ease',
        }}
      >
        <FormHeader
          textTitle={textTitle || keyword || t('Traduction Smart')}
          onBackClick={() => history.goBack()}
          onDownloadClick={() => {}}
          userRole={user?.role || ROLES.ADMINCLIENT}
        />

        <TextDetailsSection
          t={t}
          textId={textId}
          formData={{
            textTitle,
            keyword,
            url,
            titleTag,
            metaDescriptionTag,
          }}
          handlers={{
            setTextTitle,
            setKeyword,
            setUrl,
            setTitleTag,
            setMetaDescriptionTag,
          }}
        />

        {/* Two editors side by side */}
        <div className="row mt-4">
          {/* Source text editor (read-only) */}
          <div className="col-12 col-md-6">
            <div>
              <div className="label-title label-text-h">
                {t('Texte à traduire')} :
              </div>
              <div className="box-wrapper-editor">
                <CustomEditor
                  disabled={false}
                  isReadOnly={true}
                  textId={textId}
                  content={initialHtmlContent || initialTextContent}
                  onInit={(evt, editor) => handleSourceEditorInit(editor)}
                  onChange={handleSourceEditorChange}
                  t={t}
                  orderType="SMART"
                  height="60vh"
                />
              </div>
              {sourceWordCount > 0 && (
                <div className="mt-2 mb-2 text-end" style={{ fontSize: '15px', color: '#666', fontWeight: '500' }}>
                  {t('Nombre de mots')} : <strong style={{ color: '#000' }}>{sourceWordCount}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Translated text editor (editable) */}
          <div className="col-12 col-md-6">
            <div>
              <div className="label-title label-text-h">
                {t('Texte traduit')} :
              </div>
              <div className="box-wrapper-editor">
                <CustomEditor
                  disabled={false}
                  isReadOnly={false}
                  textId={textId}
                  content={actualHtmlContent || actualTextContent}
                  onInit={(evt, editor) => handleTranslatedEditorInit(editor)}
                  onChange={handleTranslatedEditorChange}
                  t={t}
                  orderType="SMART"
                  height="60vh"
                />
              </div>
              <SavingIndicator isSaving={isSaving} />
              {translatedWordCount > 0 && (
                <div className="mt-2 mb-2 text-end" style={{ fontSize: '15px', color: '#666', fontWeight: '500' }}>
                  {t('Nombre de mots')} : <strong style={{ color: '#000' }}>{translatedWordCount}</strong>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons with dropdown */}
        <div className="row mt-4">
          <div className="col-12 d-flex justify-content-end">
            {!isTextSaved ? (
              <button
                type="button"
                className="btn"
                onClick={handleManualSave}
                disabled={!actualTextContent || actualTextContent.trim().length === 0}
                style={{
                  background: (!actualTextContent || actualTextContent.trim().length === 0) ? '#f5f5f5' : '#fff8a3',
                  border: 'none',
                  color: (!actualTextContent || actualTextContent.trim().length === 0) ? '#999' : '#000',
                  borderRadius: '9999px',
                  padding: '12px 18px',
                  cursor: (!actualTextContent || actualTextContent.trim().length === 0) ? 'not-allowed' : 'pointer',
                }}
              >
                {t('Brouillon')}
              </button>
            ) : (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setActionsOpen(!actionsOpen)}
                  style={{
                    background: '#ffe426',
                    border: 'none',
                    color: '#000',
                    borderRadius: '9999px',
                    padding: '12px 18px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                    textTransform: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {t('Actions')} | <i className={`fa fa-chevron-${actionsOpen ? 'down' : 'up'}`} />
                </button>
                {actionsOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: '100%',
                      marginBottom: 8,
                      background: '#fff',
                      borderRadius: 12,
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                    }}
                  >
                    {!isTextDelivered ? (
                      <>
                        <div
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            background: '#fff8a3',
                            cursor: 'pointer'
                          }}
                          onClick={async () => {
                            setActionsOpen(false);
                            await handleSave('manualDraft');
                            setErrorSuccessMsg(t('Texte sauvegardé en brouillon'));
                            setIsSuccess(true);
                            setErrorSuccess(true);
                          }}
                        >
                          {t('Brouillon')}
                        </div>
                        <div
                          style={{ padding: '12px 16px', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, cursor: 'pointer' }}
                          onClick={async () => {
                            setActionsOpen(false);
                            await deliverText('manualDeliver', actualHtmlContent);
                            setIsTextDelivered(true);
                            if (textId) {
                              await loadTextData(textId);
                            }
                          }}
                        >
                          {t('Livrer')}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            background: '#fff8a3',
                            cursor: 'pointer'
                          }}
                          onClick={async () => {
                            setActionsOpen(false);
                            try {
                              const payload = {
                                actualTextContent: actualTextContent || '',
                                actualHtmlContent: removeStrongTags(actualHtmlContent || ''),
                              };
                              await axiosPrivate.put(`/texts/external/${textId}/validate`, payload);
                              if (textId) {
                                await loadTextData(textId);
                              }
                              setErrorSuccessMsg(t('Texte validé avec succès'));
                              setIsSuccess(true);
                              setErrorSuccess(true);
                            } catch (error) {
                              console.error('Erreur lors de la validation:', error);
                              setErrorSuccessMsg(t('Erreur lors de la validation'));
                              setIsSuccess(false);
                              setErrorSuccess(true);
                            }
                          }}
                        >
                          {t('Valider')}
                        </div>
                        <div
                          style={{ padding: '12px 16px', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, cursor: 'pointer' }}
                          onClick={async () => {
                            setActionsOpen(false);
                            try {
                              const payload = {
                                textId: textId || '',
                                deployed: true,
                                url: url || ''
                              };
                              await axiosPrivate.post('/texts/external/deploy-new-url', payload);
                              setErrorSuccessMsg(t('Texte publié avec succès'));
                              setIsSuccess(true);
                              setErrorSuccess(true);
                            } catch (error) {
                              console.error('Erreur lors de la publication:', error);
                              setErrorSuccessMsg(t('Erreur lors de la publication'));
                              setIsSuccess(false);
                              setErrorSuccess(true);
                            }
                          }}
                        >
                          {t('Publier')}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Form>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          setErrorSuccess(false);
        }}
        success={isSuccess}
        isTextDelivered={isTextDelivered}
        resetFields={() => {}}
        redactionId={textId}
        showCreditButton={false}
        executeAction={() => {}}
        toggle={() => setErrorSuccess(!errorSuccess)}
        user={user}
        textType="TRANSLATION"
        imageUrl={undefined}
      />
    </div>
  );
}

function AssignedTraductionText() {
  const { pathname } = useLocation();
  usePageTracking(pathname);

  return <AssignedTraductionTextContent />;
}

export default AssignedTraductionText;
